import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthClient, AuthStatus, Session } from "./types";

interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  client: AuthClient;
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ client, children }: { client: AuthClient; children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    let live = true;
    client.getSession().then((s) => {
      if (!live) return;
      setSession(s);
      setStatus(s ? "signed-in" : "signed-out");
    });
    const off = client.subscribe((s) => {
      setSession(s);
      setStatus(s ? "signed-in" : "signed-out");
    });
    return () => {
      live = false;
      off();
    };
  }, [client]);

  const signIn = useCallback((returnTo?: string) => client.signIn({ returnTo }), [client]);
  const signOut = useCallback(() => client.signOut(), [client]);
  const value = useMemo(() => ({ status, session, client, signIn, signOut }), [status, session, client, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Starts sign-in when signed out, preserving the current path as the return target. */
export function RequireAuth({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const { status, signIn } = useAuth();
  useEffect(() => {
    if (status === "signed-out") void signIn(window.location.pathname + window.location.search);
  }, [status, signIn]);
  if (status !== "signed-in") return <>{fallback}</>;
  return <>{children}</>;
}

/** Coarse gate on a Cognito group. The API enforces the real decision; this only avoids rendering screens the user cannot use. */
export function RequireGroup({ group, children, denied }: { group: string; children: ReactNode; denied: ReactNode }) {
  const { session } = useAuth();
  if (!session?.claims.groups.includes(group)) return <>{denied}</>;
  return <>{children}</>;
}

/** Drives the /auth/callback route. Renders nothing itself; the app decides what to show for each state. */
export function useAuthCallback(navigate: (to: string) => void): { state: "working" | "done" | "error"; error?: string } {
  const { client } = useAuth();
  const [state, setState] = useState<{ state: "working" | "done" | "error"; error?: string }>({ state: "working" });
  useEffect(() => {
    let live = true;
    client
      .handleCallback()
      .then(({ returnTo }) => {
        if (!live) return;
        setState({ state: "done" });
        navigate(returnTo);
      })
      .catch((e: unknown) => {
        if (live) setState({ state: "error", error: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      live = false;
    };
    // The callback runs once for the URL the page loaded with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);
  return state;
}
